import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Plugs, Plus, X, Lightning, CheckCircle, CircleNotch, Warning, PencilSimple, Check, Info } from '@phosphor-icons/react';
import { CopyApiButton } from '../components/CopyApiButton';

const AzureAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [injecting, setInjecting] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [form, setForm] = useState({ label: '', client_id: '', client_secret: '', tenant_id: '', subscription_id: '' });

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`/api/azure/accounts`, { withCredentials: true });
      setAccounts(res.data);
    } catch (e) {
      toast.error("Failed to fetch accounts");
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/azure/accounts`, form, { withCredentials: true });
      toast.success("Azure Account added successfully");
      setShowAdd(false);
      setForm({ label: '', client_id: '', client_secret: '', tenant_id: '', subscription_id: '' });
      fetchAccounts();
    } catch (e) {
      toast.error("Failed to add Azure account");
    }
  };

  const removeAccount = async (id) => {
    if (!window.confirm("Are you sure you want to remove this account?")) return;
    try {
      await axios.delete(`/api/azure/accounts/${id}`, { withCredentials: true });
      fetchAccounts();
      toast.success("Account removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove account");
    }
  };

  const testConnection = async (id) => {
    try {
      await axios.post(`/api/azure/accounts/${id}/test`, {}, { withCredentials: true });
      toast.success("Connection OK! Service Principal authorized.");
    } catch (e) {
      toast.error("Connection Failed: Invalid Credentials or Permissions");
    }
  };

  const saveRename = async (id) => {
    const label = editLabel.trim();
    if (!label) return toast.error("Nickname cannot be empty");
    try {
      await axios.patch(`/api/azure/accounts/${id}`, { label }, { withCredentials: true });
      setEditingId(null);
      fetchAccounts();
      toast.success("Nickname updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rename");
    }
  };

  const injectProvisions = async (id) => {
    setInjecting(prev => ({ ...prev, [id]: true }));
    try {
      const res = await axios.post(`/api/azure/accounts/${id}/inject-provisions`, {}, { withCredentials: true });
      if (res.data.status === 'ready') {
        toast.success("Provisions ready! All required Azure services are active.");
      } else {
        toast.info("Providers are still registering (~1-2 min). Re-run Inject Provisions shortly.");
      }
      await fetchAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to inject provisions");
      await fetchAccounts();
    } finally {
      setInjecting(prev => ({ ...prev, [id]: false }));
    }
  };

  const statusBadge = (acc) => {
    const s = acc.provision_status || 'unknown';
    if (s === 'ready') return { color: 'text-[#39FF14]', bg: 'bg-[#39FF14]/10 border-[#39FF14]/40', label: 'READY', Icon: CheckCircle };
    if (s === 'registering') return { color: 'text-amber-400', bg: 'bg-amber-400/10 border-amber-400/40', label: 'REGISTERING', Icon: CircleNotch };
    if (s === 'error') return { color: 'text-red-500', bg: 'bg-red-500/10 border-red-500/40', label: 'ERROR', Icon: Warning };
    return { color: 'text-slate-400', bg: 'bg-white/5 border-white/20', label: 'NOT PROVISIONED', Icon: Warning };
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-black text-[#0078D4] mb-4">AZURE ACCOUNTS</h1>
          
          <div className="bg-[#0A0B0E] border border-[#0078D4]/30 p-6 max-w-3xl space-y-4 text-sm text-slate-300 font-mono">
            <h3 className="text-[#0078D4] font-bold font-sans text-lg uppercase tracking-wider mb-2">How to Generate Azure Credentials</h3>
            
            <div className="space-y-1">
              <p><strong className="text-white">Step 1:</strong> Log into the <a href="https://portal.azure.com" target="_blank" rel="noreferrer" className="text-[#0078D4] hover:underline">Azure Portal</a>.</p>
              <p><strong className="text-white">Step 2:</strong> Click the <strong>Cloud Shell icon (`&gt;_`)</strong> in the top navigation bar, right next to the search box.</p>
              <p className="text-slate-500 text-xs ml-14">*(If this is your first time opening Cloud Shell, select "Bash", choose "No storage account required", and click Apply).*</p>
            </div>

            <div className="space-y-2 mt-4">
              <p><strong className="text-white">Step 3:</strong> Run this command to get your Subscription ID:</p>
              <code className="bg-black/50 text-white px-3 py-2 block rounded border border-white/10 select-all">
                az account show --query id -o tsv
              </code>
            </div>

            <div className="space-y-2 mt-4">
              <p><strong className="text-white">Step 4:</strong> Copy the output from Step 3, then run the command below (replace `YOUR_SUBSCRIPTION_ID` with the actual ID):</p>
              <code className="bg-black/50 text-white px-3 py-2 block rounded border border-white/10 select-all">
                az ad sp create-for-rbac --name "EmergentDeployer" --role Contributor --scopes /subscriptions/YOUR_SUBSCRIPTION_ID
              </code>
            </div>

            <div className="pt-2">
              <p><strong className="text-white">Step 5:</strong> Copy the `appId` (Client ID), `password` (Client Secret), and `tenant` (Tenant ID) from the JSON output and paste them into the "Add Account" form.</p>
            </div>
          </div>
        </div>
        <button 
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-[#0078D4] text-white px-4 py-2 font-bold hover:bg-[#005a9e] transition-colors shrink-0"
        >
          <Plus size={20} /> Add Account
        </button>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/20 p-8 w-full max-w-lg relative animate-in zoom-in-95 duration-200">
            <button onClick={() => setShowAdd(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold mb-6 text-[#0078D4]">LINK SERVICE PRINCIPAL</h2>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Label / Nickname</label>
                <input required value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#0078D4]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Subscription ID</label>
                <input required value={form.subscription_id} onChange={(e) => setForm({...form, subscription_id: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#0078D4] font-mono text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Tenant ID</label>
                <input required value={form.tenant_id} onChange={(e) => setForm({...form, tenant_id: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#0078D4] font-mono text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Client ID (App ID)</label>
                <input required value={form.client_id} onChange={(e) => setForm({...form, client_id: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#0078D4] font-mono text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Client Secret (Password)</label>
                <input required type="password" value={form.client_secret} onChange={(e) => setForm({...form, client_secret: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#0078D4] font-mono text-sm" />
              </div>
              <button type="submit" className="w-full bg-[#0078D4] text-white font-bold py-3 mt-4 hover:bg-[#005a9e] transition-colors uppercase tracking-wider">
                Link Account
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {accounts.map(acc => {
          const badge = statusBadge(acc);
          return (
          <div key={acc.id} data-testid={`azure-account-${acc.id}`} className="border border-[#0078D4]/20 bg-card p-6 relative group flex flex-col gap-4">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                {editingId === acc.id ? (
                  <div className="flex items-center gap-2">
                    <input autoFocus data-testid={`rename-input-${acc.id}`} value={editLabel} onChange={e => setEditLabel(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveRename(acc.id); if (e.key === 'Escape') setEditingId(null); }}
                      className="bg-[#0A0B0E] border border-[#0078D4]/50 px-2 py-1 text-lg font-bold focus:outline-none focus:border-[#0078D4] w-40" />
                    <button onClick={() => saveRename(acc.id)} data-testid={`rename-save-${acc.id}`} className="text-[#39FF14] hover:text-white p-1"><Check size={18} /></button>
                    <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-white p-1"><X size={18} /></button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-bold">{acc.label}</h3>
                    <button onClick={() => { setEditingId(acc.id); setEditLabel(acc.label); }} data-testid={`rename-btn-${acc.id}`} className="text-slate-500 hover:text-[#0078D4] p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Edit nickname"><PencilSimple size={16} /></button>
                  </>
                )}
                <span data-testid={`azure-status-${acc.id}`} className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 border ${badge.bg} ${badge.color}`}>
                  <badge.Icon size={12} weight="bold" /> {badge.label}
                </span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => testConnection(acc.id)} className="text-slate-400 hover:text-[#0078D4] transition-colors p-1" title="Test Connection"><Plugs size={20} /></button>
                <CopyApiButton provider="azure" accountId={acc.id} />
                <button onClick={() => removeAccount(acc.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1" title="Remove Account"><Trash size={20} /></button>
              </div>
            </div>

            <button
              data-testid={`inject-provisions-${acc.id}`}
              onClick={() => injectProvisions(acc.id)}
              disabled={injecting[acc.id]}
              className="flex items-center justify-center gap-2 bg-[#0078D4]/15 border border-[#0078D4]/50 text-[#4db8ff] px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#0078D4]/25 transition-colors disabled:opacity-60"
            >
              {injecting[acc.id] ? <CircleNotch size={16} className="animate-spin" /> : <Lightning size={16} weight="fill" />}
              {injecting[acc.id] ? 'Injecting Provisions...' : 'Inject Provisions'}
            </button>

            {Object.keys(acc.azure_providers || {}).length > 0 && (
              <div className="grid grid-cols-2 gap-1 text-[11px] font-mono">
                {Object.entries(acc.azure_providers).map(([ns, st]) => (
                  <div key={ns} className="flex items-center gap-1">
                    <span className={st === 'Registered' ? 'text-[#39FF14]' : (String(st).startsWith('error') ? 'text-red-500' : 'text-amber-400')}>●</span>
                    <span className="text-slate-400">{ns.replace('Microsoft.', '')}</span>
                  </div>
                ))}
              </div>
            )}

            {acc.azure_info && Object.keys(acc.azure_info).length > 0 && (
              <div className="bg-[#0A0B0E] border border-[#0078D4]/20 p-3 text-[11px] font-mono space-y-1.5" data-testid={`azure-info-${acc.id}`}>
                <div className="flex items-center gap-1.5 text-[#4db8ff] font-bold uppercase tracking-wider text-[10px] mb-1">
                  <Info size={13} weight="bold" /> Subscription Info
                </div>
                {acc.azure_info.subscription_name && (
                  <div className="flex justify-between gap-2"><span className="text-slate-500 shrink-0">Name</span><span className="text-white truncate">{acc.azure_info.subscription_name}</span></div>
                )}
                {acc.azure_info.offer_type && (
                  <div className="flex justify-between gap-2"><span className="text-slate-500 shrink-0">Offer</span><span className={`text-right ${/free trial|restricted/i.test(acc.azure_info.offer_type) ? 'text-amber-400' : 'text-white'}`}>{acc.azure_info.offer_type}</span></div>
                )}
                {acc.azure_info.subscription_state && (
                  <div className="flex justify-between"><span className="text-slate-500">State</span><span className={acc.azure_info.subscription_state === 'Enabled' ? 'text-[#39FF14]' : 'text-amber-400'}>{acc.azure_info.subscription_state}</span></div>
                )}
                {acc.azure_info.spending_limit && (
                  <div className="flex justify-between"><span className="text-slate-500">Spending limit</span><span className="text-white">{acc.azure_info.spending_limit}</span></div>
                )}
                <div className="flex justify-between"><span className="text-slate-500">Front Door</span><span className="text-white">{acc.azure_info.fd_profiles ?? 0} profiles · {acc.azure_info.fd_endpoints ?? 0} URLs</span></div>
                {(acc.azure_info.resource_groups != null || acc.azure_info.storage_accounts != null) && (
                  <div className="flex justify-between"><span className="text-slate-500">Resources</span><span className="text-white">{acc.azure_info.resource_groups ?? '—'} RGs · {acc.azure_info.storage_accounts ?? '—'} storage</span></div>
                )}
                {acc.azure_info.checked_at && (
                  <div className="text-slate-600 text-[10px] pt-1">Checked {new Date(acc.azure_info.checked_at).toLocaleString()}</div>
                )}
              </div>
            )}

            <div className="space-y-2 text-sm font-mono text-slate-400 border-t border-white/10 pt-4 mt-auto">
              <div className="flex justify-between">
                <span>Tier: <span className={acc.azure_tier === 'z01' ? 'text-amber-400 font-bold' : (acc.azure_tier ? 'text-[#39FF14] font-bold' : 'text-slate-500')}>{acc.azure_tier ? acc.azure_tier.toUpperCase() : 'PENDING'}</span></span>
                <span>Max URLs: <span className="text-white font-bold">{acc.max_urls || (acc.azure_tier === 'z01' ? 30 : '—')}</span></span>
              </div>
              <p>Sub ID: <span className="text-white">{acc.subscription_id}</span></p>
              <p>Client ID: <span className="text-white">{acc.client_id}</span></p>
            </div>
          </div>
          );
        })}
        {accounts.length === 0 && (
          <div className="col-span-full p-8 border border-dashed border-white/20 text-center text-slate-500">
            No Azure accounts linked.
          </div>
        )}
      </div>
    </div>
  );
};

export default AzureAccounts;